import {FieldMetaProps, FormikErrors} from 'formik';
import {get, set} from 'lodash-es';
import {OVRecommendation_pots} from '../../../../schema-types/OVRecommendation';
import {getCurrencyFloat} from '../../../../utils/currencyFormatter';
import {builderFieldsType} from '../builderFields';
import {moneyFieldValueType} from '../../../../components/AutoForm/AutoFormNew';
import {VALIDATION} from './BUILDER_VALIDATION';

export type PlanLabelType = {
  first?: string;
  second?: string;
};

export interface BuilderProps {
  contactName: string;
  sourceDestinationLabels: PlanLabelType;
}

export interface PlanTypeProps {
  id?: string;
  maxAmount?: number;
}

export interface ValidationObjectType {
  totalFields: number;
  invalidFields: number;
}

export const getValidationLevel = (validation?: ValidationObjectType): VALIDATION => {
  if (!validation) {
    return VALIDATION.NA;
  }

  const {invalidFields, totalFields} = validation;
  if (totalFields === 0 && invalidFields === 0) {
    return VALIDATION.NA;
  }
  const valid = totalFields - invalidFields;

  if (valid <= 0) {
    return VALIDATION.INVALID;
  }
  if (valid === totalFields) {
    return VALIDATION.VALID;
  }

  return VALIDATION.PARTIALLY_VALID;
};

export const getValidation = (meta?: FieldMetaProps<any>): ValidationObjectType => {
  return {
    totalFields: Object.keys(meta?.value || {}).length,
    invalidFields:
      typeof meta?.error === 'string' ? 1 : Object.keys(meta?.error || {}).length,
  };
};

export const validateAmount = ({
  sourceValues,
  amountType = 'singleClientAmount',
  errors,
  checkMaxAmount = false,
  key,
  allowZero = false,
}: {
  amountType?:
    | 'singleClientAmount'
    | 'recurringClientAmount'
    | 'singleEmployerAmount'
    | 'recurringEmployerAmount';
  sourceValues: {
    full?: boolean;
    inSpecie?: boolean;
    singleClientAmount: moneyFieldValueType;
    recurringClientAmount?: moneyFieldValueType;
    singleEmployerAmount?: moneyFieldValueType;
    recurringEmployerAmount?: moneyFieldValueType;
    maxAmount?: moneyFieldValueType;
  };
  checkMaxAmount?: boolean;
  key: string;
  allowZero?: boolean;
  errors: FormikErrors<any>;
}) => {
  if (sourceValues?.full || sourceValues?.inSpecie) {
    // automatically valid
    return;
  }
  const amount = get(sourceValues, amountType);
  if (!String(amount).length) {
    set(errors, key, 'Amount required');
    return;
  }
  // the amount field is usually a string
  const amountNum = getCurrencyFloat(amount!);
  if (allowZero) {
    if (amountNum < 0) {
      set(errors, key, 'Amount cannot be negative');
    }
  } else if (amountNum <= 0) {
    set(errors, key, 'Amount must be greater than zero');
  }
  const maxAmountStr = get(sourceValues, 'maxAmount');
  const maxAmount = maxAmountStr ? getCurrencyFloat(maxAmountStr) : 0;
  if (maxAmount && checkMaxAmount) {
    if (Number(amount) > maxAmount) {
      set(errors, key, 'Amount set must be less than available amount');
    }
  }
};

export const validateTextField = (
  value: string | undefined | null,
  key: string,
  errors: FormikErrors<builderFieldsType>
) => {
  if (value && value?.length > 0) {
    return;
  }
  set(errors, key, 'Required');
};

export const validateMandateField = (
  value: string | undefined | null | {id: string},
  key: string,
  errors: FormikErrors<builderFieldsType>
) => {
  if (value && typeof value === 'string' && value?.length > 0) {
    return;
  }
  if (value && typeof value === 'object' && value?.id?.length > 0) {
    return;
  }
  set(errors, key, 'Mandate is required');
};
